import { UseQueryOptions, useQuery } from 'react-query';
import { useState, useCallback, useMemo, useEffect } from 'react';
import clioDocumentPickerService from '~/src/services/api/clioDocumentPickerService';
import {
  ClioDocument,
  DocumentSearchParams,
  DocumentTemplateSearchParams,
  DocumentTemplateSearchQuery,
  ViewMode,
  SortBy,
  SortOrder,
  BreadcrumbItem,
  ClioApiResponseMatterRootFolder,
} from '~/src/types/clioDocumentPicker';
import { DEFAULT_DOCUMENT_CONTENT_TYPES } from '~/src/constants/documentMimeTypes';
import { APIError } from '../utils/error';
import { getOrgFprint } from '../entities/user';

const FIVE_MINUTES = 5 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;
const TWO_MINUTES = 2 * 60 * 1000;

// Utility hook for debouncing
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Hook for searching documents
export const useClioDocumentSearch = (
  orgId: string,
  query: string,
  params: Omit<DocumentSearchParams, 'q'> = {},
) => {
  const debouncedQuery = useDebounce(query, 300);

  return useQuery(
    ['clioDocumentSearch', orgId, debouncedQuery, params],
    () =>
      clioDocumentPickerService.searchDocuments(orgId, debouncedQuery, params),
    {
      enabled: !!orgId && !!debouncedQuery.trim(),
      staleTime: TWO_MINUTES,
      cacheTime: FIVE_MINUTES,
    },
  );
};

// Hook for recent documents
export const useClioRecentDocuments = (
  orgId: string,
  limit: number = 20,
  days: number = 30,
  contentTypes?: string[],
) => {
  return useQuery(
    ['clioRecentDocuments', orgId, limit, days, contentTypes],
    () =>
      clioDocumentPickerService.getRecentDocuments(
        orgId,
        limit,
        days,
        contentTypes,
      ),
    {
      enabled: !!orgId,
      staleTime: FIVE_MINUTES,
      cacheTime: TEN_MINUTES,
    },
  );
};

// Hook for listing document templates
export const useClioDocumentTemplates = (
  orgId: string,
  params: DocumentTemplateSearchParams = {},
) => {
  return useQuery(
    ['clioDocumentTemplates', orgId, params],
    () =>
      clioDocumentPickerService.listDocumentTemplates(orgId, {
        ...params,
        content_types: params.content_types || DEFAULT_DOCUMENT_CONTENT_TYPES,
      }),
    {
      enabled: !!orgId,
      staleTime: FIVE_MINUTES,
      cacheTime: TEN_MINUTES,
    },
  );
};

// Hook for searching document templates
export const useClioDocumentTemplateSearch = (
  orgId: string,
  query: string,
  params: Omit<DocumentTemplateSearchQuery, 'query'> = {},
) => {
  const debouncedQuery = useDebounce(query, 300);

  return useQuery(
    ['clioDocumentTemplateSearch', orgId, debouncedQuery, params],
    () =>
      debouncedQuery.trim()
        ? clioDocumentPickerService.searchDocumentTemplates(orgId, {
            ...params,
            query: debouncedQuery,
            content_types:
              params.content_types || DEFAULT_DOCUMENT_CONTENT_TYPES,
          })
        : { document_templates: [], meta: { records: 0 } },
    {
      enabled: !!orgId && !!debouncedQuery.trim(),
      staleTime: TWO_MINUTES,
      cacheTime: FIVE_MINUTES,
    },
  );
};

// Hook for folder contents (documents + folders)
export const useClioFolderContents = (
  orgId: string,
  folderId: number | null = null,
  params: {
    page_token?: string;
    limit?: number;
    matter_id?: number;
    content_types?: string[];
  } = {},
) => {
  return useQuery(
    ['clioFolderContents', orgId, folderId, params],
    () => clioDocumentPickerService.getFolderContents(orgId, folderId, params),
    {
      enabled: !!orgId,
      staleTime: FIVE_MINUTES,
      cacheTime: TEN_MINUTES,
      // Force refetch when folder changes to ensure fresh data
      refetchOnMount: true,
    },
  );
};

export const useClioMatterFolder = (
  matter_id: string,
  shouldOpen: boolean,
  options: UseQueryOptions<
    ClioApiResponseMatterRootFolder,
    APIError,
    ClioApiResponseMatterRootFolder,
    string[]
  > = {},
) => {
  const orgId = getOrgFprint();
  return useQuery(
    ['clioMatterRootFolder', orgId, matter_id],
    () => clioDocumentPickerService.getMatterFolder(orgId, matter_id),
    {
      enabled: !!orgId && shouldOpen,
      staleTime: FIVE_MINUTES,
      cacheTime: TEN_MINUTES,
      // Force refetch when folder changes to ensure fresh data
      refetchOnMount: true,
      ...options,
    },
  );
};

// Main document picker state management hook
export const useDocumentPickerState = (
  orgId: string,
  initialMatterId?: number,
) => {
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<ClioDocument | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('updated_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedMatter, setSelectedMatter] = useState<number | null>(
    initialMatterId || null,
  );
  const [showRecentDocuments, setShowRecentDocuments] = useState(false);

  // State to persist breadcrumbs during navigation
  const [persistedBreadcrumbs, setPersistedBreadcrumbs] = useState<
    BreadcrumbItem[]
  >([]);

  // Pagination state - removed accumulation, now using direct data
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(
    undefined,
  );
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(
    undefined,
  );

  // Determine which data to fetch based on current state
  const shouldSearch = searchQuery.trim().length > 0;
  const shouldShowRecent = showRecentDocuments && !shouldSearch;

  // Always filter to Word documents only by default
  const contentTypesParam = DEFAULT_DOCUMENT_CONTENT_TYPES;

  // Fetch folder contents (default view)
  const folderContentsQuery = useClioFolderContents(orgId, currentFolderId, {
    matter_id: selectedMatter || undefined,
    limit: 50,
    page_token: currentPageToken,
    content_types: contentTypesParam,
  });

  // Search documents
  const searchQueryResult = useClioDocumentSearch(orgId, searchQuery, {
    matter_id: selectedMatter || undefined,
    parent_id: currentFolderId || undefined,
    limit: 50,
    page_token: currentPageToken,
    content_types: contentTypesParam,
  });

  // Recent documents
  const recentDocumentsQuery = useClioRecentDocuments(
    orgId,
    50,
    30,
    contentTypesParam,
  );

  // Determine current data source
  const currentQuery = shouldSearch
    ? searchQueryResult
    : shouldShowRecent
    ? recentDocumentsQuery
    : folderContentsQuery;

  // Handle pagination token updates when new results arrive
  useEffect(() => {
    if (!currentQuery.data) return;

    // Update next page token for future pagination
    setNextPageToken(currentQuery.data.next_page_token);
  }, [currentQuery.data]);

  // Use direct data from query for display (no accumulation)
  const documents = useMemo(() => {
    if (!currentQuery.data) return [];

    return shouldSearch
      ? currentQuery.data.documents || []
      : shouldShowRecent
      ? currentQuery.data.documents || []
      : currentQuery.data.documents || [];
  }, [currentQuery.data, shouldSearch, shouldShowRecent]);

  const folders = useMemo(() => {
    if (!currentQuery.data) return [];

    return shouldSearch || shouldShowRecent
      ? []
      : currentQuery.data.folders || [];
  }, [currentQuery.data, shouldSearch, shouldShowRecent]);

  // Update persisted breadcrumbs when new data arrives
  useEffect(() => {
    if (
      !shouldSearch &&
      !shouldShowRecent &&
      folderContentsQuery.data?.breadcrumbs
    ) {
      setPersistedBreadcrumbs(folderContentsQuery.data.breadcrumbs);
    } else if (shouldSearch || shouldShowRecent) {
      setPersistedBreadcrumbs([]);
    }
  }, [shouldSearch, shouldShowRecent, folderContentsQuery.data?.breadcrumbs]);

  const breadcrumbs = useMemo(() => {
    if (shouldSearch || shouldShowRecent) {
      return [];
    }

    // Return new breadcrumbs if available, otherwise persist the previous ones
    return folderContentsQuery.data?.breadcrumbs || persistedBreadcrumbs;
  }, [
    shouldSearch,
    shouldShowRecent,
    folderContentsQuery.data?.breadcrumbs,
    persistedBreadcrumbs,
  ]);

  // Navigation functions - Reset pagination tokens on navigation
  const navigateToFolder = useCallback((folderId: number | null) => {
    // Reset pagination tokens
    setCurrentPageToken(undefined);
    setNextPageToken(undefined);

    // Update navigation state
    setCurrentFolderId(folderId);
    setSearchQuery('');
    setShowRecentDocuments(false);
  }, []);

  const navigateBack = useCallback(() => {
    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      const parentIndex = breadcrumbs.length - 2;
      if (parentIndex < 0) {
        // Already at root, can't go back further
        return;
      }
      // If going back to root (index 0), navigate to top-level view (null)
      const parentId =
        parentIndex === 0 ? null : breadcrumbs[parentIndex]?.id || null;
      navigateToFolder(parentId);
    }
  }, [breadcrumbs, navigateToFolder]);

  const showRecent = useCallback(() => {
    setShowRecentDocuments(true);
    setSearchQuery('');
    setCurrentFolderId(null);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setShowRecentDocuments(false);
    setCurrentPageToken(undefined);
    setNextPageToken(undefined);
  }, []);

  // Page navigation functionality
  const setPageToken = useCallback((pageToken: string | undefined) => {
    setCurrentPageToken(pageToken);
  }, []);

  // Sort documents
  const sortedDocuments = useMemo(() => {
    const sorted = [...documents];
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.filename.toLowerCase();
          bValue = b.filename.toLowerCase();
          break;
        case 'size':
          aValue = a.size || 0;
          bValue = b.size || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || a.updated_at);
          bValue = new Date(b.created_at || b.updated_at);
          break;
        case 'updated_at':
        default:
          aValue = new Date(a.updated_at);
          bValue = new Date(b.updated_at);
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [documents, sortBy, sortOrder]);

  return {
    // State
    currentFolderId,
    selectedDocument,
    viewMode,
    searchQuery,
    sortBy,
    sortOrder,
    selectedMatter,
    showRecentDocuments,

    // Data
    documents: sortedDocuments,
    folders,
    breadcrumbs,

    // Loading states
    isLoading: currentQuery.isLoading,
    isFetching: currentQuery.isFetching,
    error: currentQuery.error,

    // Pagination
    hasMore: !!nextPageToken,
    nextPageToken,

    // Actions
    setSelectedDocument,
    setViewMode,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setSelectedMatter,
    navigateToFolder,
    navigateBack,
    showRecent,
    clearSearch,
    setPageToken,

    // Utilities
    canGoBack: Array.isArray(breadcrumbs) && breadcrumbs.length > 1,
    refetch: currentQuery.refetch,
  };
};
